import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import type { CompanyRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import { CompanyForm, type CompanyFormValues } from "@/modules/companies/components/CompanyForm";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { companyMutations } from "@/queries/companies/mutations";
import { useCompanyQueries } from "@/queries/companies/queryOptions";
import { routes } from "@/routes";

export function CreateCompanyPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const companyQueries = useCompanyQueries();
  const companiesQuery = useQuery(companyQueries.list());
  const addressQueries = useAddressQueries();
  const addressesQuery = useQuery(addressQueries.list());
  const createCompany = companyMutations.useAddCompany();
  const error = companiesQuery.error ?? addressesQuery.error;

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (companiesQuery.isPending || addressesQuery.isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const onSubmit = async (data: CompanyFormValues) => {
    const payload = await createCompanyPayload(data, uploadFormImage);
    await createCompany.mutateAsync(payload);
    void navigate(routes.companies.list({ slug }));
  };

  return (
    <CompanyForm
      isEditMode={false}
      defaultValues={{}}
      isSubmitting={isUploadingImage || createCompany.isPending}
      existingCompanies={companiesQuery.data?.items ?? []}
      addresses={addressesQuery.data?.items ?? []}
      onSubmit={onSubmit}
    />
  );
}

async function createCompanyPayload(
  data: CompanyFormValues,
  uploadFormImage: ReturnType<typeof useUploadImage>["uploadFormImage"],
): Promise<CompanyRequest> {
  const payload: CompanyRequest = {
    addressId: data.address.id,
    name: data.name,
    description: data.description,
    externalId: data.externalId,
  };

  const background = data.background.at(0);
  if (background) {
    payload.backgroundImageId = (await uploadFormImage(background))?.id;
  }
  const logo = data.logo.at(0);
  if (logo) {
    payload.logoId = (await uploadFormImage(logo))?.id;
  }

  return payload;
}
